import React, { useEffect, useState } from 'react';
import { useImageUrl } from '@shared/components/ImageContext';
import type { HistoryRange } from '@shared/history';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  RESULT_OPTIONS,
  TIME_PERIODS,
  type ResultFilter,
} from './historyFilters';
import styles from './RunList.module.scss';

interface HistoryFiltersDialogProps {
  open: boolean;
  range: HistoryRange;
  result: ResultFilter;
  onApply: (range: HistoryRange, result: ResultFilter) => void;
  onClose: () => void;
}

const DEFAULTS: { range: HistoryRange; result: ResultFilter } = {
  range: 'D7',
  result: 'ALL',
};

// Picks nothing on its own: the list only changes on Apply.
const HistoryFiltersDialog: React.FC<HistoryFiltersDialogProps> = ({
  open,
  range,
  result,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const { img, imgProps } = useImageUrl();
  const [draftRange, setDraftRange] = useState(range);
  const [draftResult, setDraftResult] = useState(result);

  // Each opening starts from what the list is actually showing.
  useEffect(() => {
    if (open) {
      setDraftRange(range);
      setDraftResult(result);
    }
  }, [open, range, result]);

  if (!open) return null;

  const skin = (primary: boolean) =>
    ({
      '--button-skin': `url(${img(
        primary ? 'ButtonSkin_Primary' : 'ButtonSkin_Secondary'
      )})`,
    }) as React.CSSProperties;

  return (
    <div className={styles.filterScrim}>
      <div
        className={styles.filterDialog}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.filterHead}>
          <h2 className={styles.filterTitle}>{t('History filters')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={onClose}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        <p className={styles.filterGroup}>{t('Time period')}</p>
        {TIME_PERIODS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            className={cn(styles.option, styles.tab_off)}
            style={skin(false)}
            onClick={() => setDraftRange(value)}
          >
            {`${draftRange === value ? '●' : '○'}  ${t(label)}`}
          </button>
        ))}

        <p className={styles.filterGroup}>{t('Round result')}</p>
        <div className={styles.resultRow}>
          {RESULT_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={cn(styles.option, styles.option_slim, styles.tab_off)}
              style={skin(false)}
              onClick={() => setDraftResult(value)}
            >
              {draftResult === value ? `● ${t(label)}` : t(label)}
            </button>
          ))}
        </div>

        <p className={styles.notice}>
          {t(
            'Win/loss is the game result. Profit is shown separately in each round.'
          )}
        </p>

        <button
          type="button"
          className={cn(styles.apply, styles.tab_on)}
          style={skin(true)}
          onClick={() => onApply(draftRange, draftResult)}
        >
          {t('APPLY FILTERS')}
        </button>
        <button
          type="button"
          className={cn(styles.apply, styles.tab_off)}
          style={skin(false)}
          onClick={() => onApply(DEFAULTS.range, DEFAULTS.result)}
        >
          {t('RESET FILTERS')}
        </button>
      </div>
    </div>
  );
};

export default HistoryFiltersDialog;
